import { notifications } from '@mantine/notifications';
import { AxiosError } from 'axios';
import { RevisionTemplateGetResponse } from 'api/actions/revision-template-get/revision-template-get-response';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import RevisionTemplateForm, {
  RevisionTemplateFormData,
} from 'components/forms/revision-template/RevisionTemplateForm';
import { REVISION_TEMPLATE_REDIRECT_AFTER_SAVE } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH, REVISION_TEMPLATES_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Determines the restriction type of the revision template.
 */
function getRevisionTemplateRestriction(data: RevisionTemplateGetResponse) {
  if (!data.isGlobal && data.isOrganization) {
    return 'organization';
  }

  return 'global';
}

/**
 * Page used to edit a revision template.
 */
export default function EditRevisionTemplatePage() {
  const { getAction } = useApi();
  const navigate = useNavigate();

  const { revisionTemplateId } = useParams();

  const { data, loading, error } = useImmediateAction(() => {
    const action = getAction('RevisionTemplateGet');
    const permissions = getAction('RevisionTemplateCheckPermissions');

    const parameters = { revisionTemplateId: String(revisionTemplateId) };
    return Promise.all([action({ parameters }), permissions({ parameters })]).then(([template, permissions]) => ({
      template,
      permissions,
    }));
  });

  if (!revisionTemplateId) {
    return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  }

  if (error instanceof AxiosError && error.response?.status === 404) {
    return <Navigate to={REVISION_TEMPLATES_PAGE_PATH.original} replace />;
  }

  if (error) {
    panic(error);
    return <></>;
  }

  const template = data?.template;
  const canEdit = data?.permissions.canEdit ?? false;
  if (data && !data.permissions.canEdit && !data.permissions.canManageBlacklist) {
    return <Navigate to={REVISION_TEMPLATES_PAGE_PATH.original} replace />;
  }

  /**
   * Handles the form submission.
   */
  function onSubmit({
    name,
    kinds,
    restriction,
    organizations,
    blacklistedOrganizations = [],
    supportsKep,
    singleDeviceOnly,
  }: RevisionTemplateFormData) {
    const parameters = { revisionTemplateId: String(revisionTemplateId) };
    const isOrganization = restriction === 'organization';
    const blacklist = blacklistedOrganizations.map(({ organizationId }) => organizationId);
    const save = canEdit
      ? getAction('RevisionTemplateUpdate')({
          parameters,
          payload: {
            name,
            kinds,
            isGlobal: restriction === 'global',
            isOrganization,
            organizations: isOrganization ? organizations.map(({ organizationId }) => organizationId) : [],
            blacklistedOrganizations: isOrganization ? [] : blacklist,
            supportsKep,
            singleDeviceOnly,
          },
        })
      : getAction('RevisionTemplateSetBlacklist')({ parameters, payload: { blacklistedOrganizations: blacklist } });

    return save.then(() => {
      notifications.show({
        title: 'Šablóna revíznej správy bola úspešne upravená',
        message: `Šablóna revíznej správy ${name} bola úspešne upravená.`,
        color: SUCCESS_NOTIFICATION_COLOR,
      });
    });
  }

  /** Redirects after the form has cleared its dirty state. */
  function onSaved() {
    if (REVISION_TEMPLATE_REDIRECT_AFTER_SAVE === 'list') {
      navigate(REVISION_TEMPLATES_PAGE_PATH.original);
    }
  }

  return (
    <DashboardLayout
      title={`Úprava šablóny revíznej správy: ${template?.name}`}
      breadcrumbs={[
        { title: 'Šablóny revíznych správ', link: REVISION_TEMPLATES_PAGE_PATH.original },
        { title: template?.name ?? 'Načítavanie ...' },
      ]}
    >
      {!loading && template && (
        <RevisionTemplateForm
          initialValues={{
            name: template.name,
            kinds: template.kinds,
            restriction: getRevisionTemplateRestriction(template),
            organizations: template.organizations ?? [],
            blacklistedOrganizations: template.blacklistedOrganizations,
            slug: template.slug,
            deviceTypeId: String(template.deviceTypeId),
            supportsKep: template.supportsKep,
            singleDeviceOnly: template.singleDeviceOnly,
          }}
          onSubmit={onSubmit}
          onSaved={onSaved}
          metadataReadOnly={!canEdit}
          readOnly={{ deviceTypeId: true }}
        />
      )}
    </DashboardLayout>
  );
}
